/**
 * Collection Page JavaScript
 * Handles filter drawer, price slider, accordions, and sorting
 */

(function() {
  'use strict';

  // ==========================================================================
  // FILTER DRAWER MODULE
  // ==========================================================================
  
  const FilterDrawer = {
    drawer: null,
    overlay: null,
    toggleBtn: null,
    closeBtn: null,
    isOpen: false,

    init() {
      this.drawer = document.querySelector('[data-filter-drawer]');
      this.overlay = document.querySelector('[data-filter-overlay]');
      this.toggleBtn = document.querySelector('[data-filter-toggle]');
      this.closeBtn = document.querySelector('[data-filter-close]');

      if (!this.drawer) return;

      this.bindEvents();
    },

    bindEvents() {
      if (this.toggleBtn) {
        this.toggleBtn.addEventListener('click', () => this.open());
      }

      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', () => this.close());
      }

      if (this.overlay) {
        this.overlay.addEventListener('click', () => this.close());
      }

      // Close on escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) {
          this.close();
        }
      });
    },

    open() {
      this.drawer.classList.add('is-active');
      this.overlay.classList.add('is-active');
      document.body.style.overflow = 'hidden';
      this.isOpen = true;
      
      // Focus first focusable element
      const firstFocusable = this.drawer.querySelector('button, input, select, a');
      if (firstFocusable) firstFocusable.focus();
    },

    close() {
      this.drawer.classList.remove('is-active');
      this.overlay.classList.remove('is-active');
      document.body.style.overflow = '';
      this.isOpen = false;
      
      // Return focus to toggle button
      if (this.toggleBtn) this.toggleBtn.focus();
    },

    toggle() {
      this.isOpen ? this.close() : this.open();
    }
  };

  // ==========================================================================
  // FILTER HANDLER MODULE
  // ==========================================================================
  
  const FilterHandler = {
    init() {
      this.bindCheckboxEvents();
      this.bindPriceSliderEvents();
      this.initFromURL();
    },

    syncMatchingCheckboxes(sourceCheckbox) {
      if (!sourceCheckbox) return;
      const filterType = sourceCheckbox.dataset.filterType;
      const filterValue = sourceCheckbox.dataset.filterValue;
      if (!filterType || filterValue == null) return;

      const selector = `[data-filter-checkbox][data-filter-type="${CSS.escape(filterType)}"][data-filter-value="${CSS.escape(filterValue)}"]`;
      const matches = document.querySelectorAll(selector);
      matches.forEach((checkbox) => {
        if (checkbox === sourceCheckbox) return;
        checkbox.checked = sourceCheckbox.checked;
      });
    },

    // Initialize checkboxes from current URL params
    initFromURL() {
      const url = new URL(window.location.href);
      const checkboxes = document.querySelectorAll('[data-filter-checkbox]');
      
      checkboxes.forEach(checkbox => {
        const filterType = checkbox.dataset.filterType;
        const filterValue = checkbox.dataset.filterValue;
        
        if (filterType && filterValue) {
          const paramValues = url.searchParams.getAll(filterType);
          if (paramValues.includes(filterValue)) {
            checkbox.checked = true;
          }
        }
      });
    },

    bindCheckboxEvents() {
      const checkboxes = document.querySelectorAll('[data-filter-checkbox]');
      
      checkboxes.forEach(checkbox => {
        checkbox.addEventListener('change', () => {
          // Desktop + drawer render duplicates; keep them in sync.
          this.syncMatchingCheckboxes(checkbox);
          this.applyFilters();
        });
      });
    },

    bindPriceSliderEvents() {
      const sliders = document.querySelectorAll('[data-price-slider]');
      
      sliders.forEach(slider => {
        slider.addEventListener('pricechange', (e) => {
          const { min, max } = e.detail;
          this.applyFilters(min, max);
        });
      });
    },

    applyFilters(priceMin = null, priceMax = null) {
      const url = new URL(window.location.href);

      // Preserve current price params unless slider is actively changing them.
      const existingPriceMin = url.searchParams.get('filter.v.price.gte');
      const existingPriceMax = url.searchParams.get('filter.v.price.lte');
      
      // Clear existing filter params (but keep sort_by)
      const sortBy = url.searchParams.get('sort_by');

      // Reset pagination when filters change
      url.searchParams.delete('page');

      const paramsToRemove = [];
      url.searchParams.forEach((value, key) => {
        if (key.startsWith('filter.')) {
          paramsToRemove.push(key);
        }
      });
      paramsToRemove.forEach(key => url.searchParams.delete(key));
      
      // Restore sort_by if it existed
      if (sortBy) {
        url.searchParams.set('sort_by', sortBy);
      }

      // Collect all checked checkboxes
      const checkboxes = document.querySelectorAll('[data-filter-checkbox]:checked');

      // Deduplicate because the same filter exists in desktop + drawer markup.
      const uniquePairs = new Set();
      checkboxes.forEach((checkbox) => {
        const filterType = checkbox.dataset.filterType;
        const filterValue = checkbox.dataset.filterValue;
        if (!filterType || filterValue == null) return;

        const key = `${filterType}::${filterValue}`;
        if (uniquePairs.has(key)) return;
        uniquePairs.add(key);

        url.searchParams.append(filterType, filterValue);
      });

      // Add price filter if provided
      const finalPriceMin = priceMin !== null ? priceMin : existingPriceMin;
      const finalPriceMax = priceMax !== null ? priceMax : existingPriceMax;
      if (finalPriceMin !== null && finalPriceMin !== '') {
        url.searchParams.set('filter.v.price.gte', finalPriceMin);
      }
      if (finalPriceMax !== null && finalPriceMax !== '') {
        url.searchParams.set('filter.v.price.lte', finalPriceMax);
      }

      // Navigate to filtered URL
      window.location.href = url.toString();
    }
  };

  // ==========================================================================
  // PRICE SLIDER MODULE
  // ==========================================================================
  
  const PriceSlider = {
    sliders: [],

    init() {
      const sliderElements = document.querySelectorAll('[data-price-slider]');
      sliderElements.forEach(el => this.initSlider(el));
    },

    initSlider(container) {
      const minKnob = container.querySelector('[data-price-knob="min"]');
      const maxKnob = container.querySelector('[data-price-knob="max"]');
      const activeBar = container.querySelector('[data-price-active-bar]');
      const minDisplay = container.querySelector('[data-price-min-display]');
      const maxDisplay = container.querySelector('[data-price-max-display]');
      const track = container.querySelector('.price-slider__track');

      if (!minKnob || !maxKnob || !track) return;

      const min = parseInt(container.dataset.min) || 0;
      const max = parseInt(container.dataset.max) || 10000;

      const state = {
        minValue: min,
        maxValue: max,
        min,
        max,
        isDragging: null
      };

      const updateUI = () => {
        const minPercent = ((state.minValue - state.min) / (state.max - state.min)) * 100;
        const maxPercent = ((state.maxValue - state.min) / (state.max - state.min)) * 100;

        minKnob.style.left = `${minPercent}%`;
        maxKnob.style.left = `${maxPercent}%`;
        
        if (activeBar) {
          activeBar.style.left = `${minPercent}%`;
          activeBar.style.width = `${maxPercent - minPercent}%`;
        }

        if (minDisplay) minDisplay.textContent = `£ ${state.minValue}`;
        if (maxDisplay) {
          const suffix = state.maxValue >= state.max ? '+' : '';
          maxDisplay.textContent = `£ ${state.maxValue}${suffix}`;
        }
      };

      const handleDrag = (e, knobType) => {
        const rect = track.getBoundingClientRect();
        const clientX = e.type.includes('touch') ? e.touches[0].clientX : e.clientX;
        let percent = ((clientX - rect.left) / rect.width) * 100;
        percent = Math.max(0, Math.min(100, percent));
        
        const value = Math.round((percent / 100) * (state.max - state.min) + state.min);

        if (knobType === 'min') {
          state.minValue = Math.min(value, state.maxValue - 1);
        } else {
          state.maxValue = Math.max(value, state.minValue + 1);
        }

        updateUI();
      };

      const startDrag = (e, knobType) => {
        e.preventDefault();
        state.isDragging = knobType;

        const onMove = (moveEvent) => {
          if (state.isDragging) {
            handleDrag(moveEvent, state.isDragging);
          }
        };

        const onEnd = () => {
          state.isDragging = null;
          document.removeEventListener('mousemove', onMove);
          document.removeEventListener('mouseup', onEnd);
          document.removeEventListener('touchmove', onMove);
          document.removeEventListener('touchend', onEnd);
          
          // Dispatch custom event for filtering
          container.dispatchEvent(new CustomEvent('pricechange', {
            detail: { min: state.minValue, max: state.maxValue }
          }));
        };

        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onEnd);
        document.addEventListener('touchmove', onMove);
        document.addEventListener('touchend', onEnd);
      };

      minKnob.addEventListener('mousedown', (e) => startDrag(e, 'min'));
      minKnob.addEventListener('touchstart', (e) => startDrag(e, 'min'));
      maxKnob.addEventListener('mousedown', (e) => startDrag(e, 'max'));
      maxKnob.addEventListener('touchstart', (e) => startDrag(e, 'max'));

      // Initialize from URL params if present
      const url = new URL(window.location.href);
      const urlMin = parseInt(url.searchParams.get('filter.v.price.gte'), 10);
      const urlMax = parseInt(url.searchParams.get('filter.v.price.lte'), 10);

      if (!Number.isNaN(urlMin)) {
        state.minValue = Math.max(state.min, Math.min(urlMin, state.max));
      }

      if (!Number.isNaN(urlMax)) {
        state.maxValue = Math.max(state.min, Math.min(urlMax, state.max));
      }

      if (state.minValue >= state.maxValue) {
        state.minValue = Math.max(state.min, state.maxValue - 1);
      }

      // Initial UI update
      updateUI();

      this.sliders.push({ container, state, updateUI });
    }
  };

  // ==========================================================================
  // ACCORDION MODULE
  // ==========================================================================
  
  const Accordion = {
    init() {
      const toggles = document.querySelectorAll('[data-filter-toggle-section]');
      toggles.forEach(toggle => {
        toggle.addEventListener('click', (e) => {
          // Prevent toggling if clicking on search icon
          if (e.target.closest('[data-brand-search-toggle]')) return;
          
          const section = toggle.closest('[data-filter-section]');
          if (section) {
            section.classList.toggle('is-collapsed');
          }
        });
      });
    }
  };

  // ==========================================================================
  // BRAND FILTER MODULE
  // ==========================================================================
  
  const BrandFilter = {
    init() {
      this.initSearchToggle();
      this.initSearch();
      this.initShowMore();
    },

    initSearchToggle() {
      const toggles = document.querySelectorAll('[data-brand-search-toggle]');
      
      toggles.forEach(toggle => {
        toggle.addEventListener('click', (e) => {
          e.stopPropagation();
          const section = toggle.closest('[data-filter-section]');
          if (!section) return;
          
          const input = section.querySelector('[data-brand-search-input]');
          if (input) {
            input.classList.toggle('is-visible');
            if (input.classList.contains('is-visible')) {
              input.focus();
            } else {
              input.value = '';
              // Reset search results
              this.resetSearch(section);
            }
          }
        });
      });
    },

    initSearch() {
      const inputs = document.querySelectorAll('[data-brand-search-input]');
      
      inputs.forEach(input => {
        // Debounce for performance
        let debounceTimer;
        
        input.addEventListener('input', () => {
          clearTimeout(debounceTimer);
          debounceTimer = setTimeout(() => {
            this.filterBrands(input);
          }, 150);
        });

        // Prevent form submission on enter
        input.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
          }
        });
      });
    },

    filterBrands(input) {
      const section = input.closest('[data-filter-section]');
      if (!section) return;
      
      const query = input.value.toLowerCase().trim();
      const brandItems = section.querySelectorAll('[data-brand-item]');
      const noResults = section.querySelector('[data-brand-no-results]');
      const showMoreBtn = section.querySelector('[data-brand-show-more]');
      
      let visibleCount = 0;
      
      brandItems.forEach(item => {
        const brandName = item.dataset.brandName || '';
        
        if (query === '') {
          // Reset to default state (respecting show more/less)
          item.classList.remove('is-search-hidden');
          visibleCount++;
        } else if (brandName.includes(query)) {
          // Show matching items and remove collapsed state when searching
          item.classList.remove('is-search-hidden');
          item.classList.remove('is-hidden');
          visibleCount++;
        } else {
          item.classList.add('is-search-hidden');
        }
      });
      
      // Show/hide "no results" message
      if (noResults) {
        noResults.classList.toggle('is-visible', visibleCount === 0 && query !== '');
      }
      
      // Hide show more button when searching
      if (showMoreBtn) {
        showMoreBtn.classList.toggle('is-hidden', query !== '');
      }
      
      // If search is cleared, restore default state
      if (query === '') {
        this.resetSearch(section);
      }
    },

    resetSearch(section) {
      const brandItems = section.querySelectorAll('[data-brand-item]');
      const showMoreBtn = section.querySelector('[data-brand-show-more]');
      const noResults = section.querySelector('[data-brand-no-results]');
      
      // Get limit from show more button or default to 5
      const limit = showMoreBtn ? parseInt(showMoreBtn.dataset.limit) || 5 : 5;
      const isCollapsed = showMoreBtn ? showMoreBtn.dataset.collapsed === 'true' : true;
      
      brandItems.forEach((item, index) => {
        item.classList.remove('is-search-hidden');
        
        // Restore collapsed state
        if (isCollapsed && index >= limit) {
          item.classList.add('is-hidden');
        }
      });
      
      // Hide no results message
      if (noResults) {
        noResults.classList.remove('is-visible');
      }
      
      // Show the show more button again
      if (showMoreBtn) {
        showMoreBtn.classList.remove('is-hidden');
      }
    },

    initShowMore() {
      const buttons = document.querySelectorAll('[data-brand-show-more]');
      
      buttons.forEach(btn => {
        btn.addEventListener('click', () => {
          const section = btn.closest('[data-filter-section]');
          if (!section) return;
          
          const brandItems = section.querySelectorAll('[data-brand-item]');
          const isCollapsed = btn.dataset.collapsed === 'true';
          const limit = parseInt(btn.dataset.limit) || 5;
          
          const moreText = btn.querySelector('[data-more-text]');
          const lessText = btn.querySelector('[data-less-text]');
          
          if (isCollapsed) {
            // Expand - show all brands
            brandItems.forEach(item => {
              item.classList.remove('is-hidden');
            });
            btn.dataset.collapsed = 'false';
            if (moreText) moreText.style.display = 'none';
            if (lessText) lessText.style.display = '';
          } else {
            // Collapse - hide brands beyond limit
            brandItems.forEach((item, index) => {
              if (index >= limit) {
                item.classList.add('is-hidden');
              }
            });
            btn.dataset.collapsed = 'true';
            if (moreText) moreText.style.display = '';
            if (lessText) lessText.style.display = 'none';
          }
        });
      });
    }
  };

  const ColorFilter = {
    init() {
      this.initShowMore();
    },

    initShowMore() {
      const buttons = document.querySelectorAll('[data-color-show-more]');
      
      buttons.forEach(btn => {
        btn.addEventListener('click', () => {
          const section = btn.closest('[data-filter-section]');
          if (!section) return;
          
          const colorItems = section.querySelectorAll('[data-color-item]');
          const isCollapsed = btn.dataset.collapsed === 'true';
          const limit = parseInt(btn.dataset.limit) || 5;
          
          const moreText = btn.querySelector('[data-more-text]');
          const lessText = btn.querySelector('[data-less-text]');
          
          if (isCollapsed) {
            // Expand
            colorItems.forEach(item => {
              item.classList.remove('is-hidden');
            });
            
            btn.dataset.collapsed = 'false';
            if (moreText) moreText.style.display = 'none';
            if (lessText) lessText.style.display = '';
          } else {
            // Collapse
            colorItems.forEach((item, index) => {
              if (index >= limit) {
                item.classList.add('is-hidden');
              }
            });
            
            btn.dataset.collapsed = 'true';
            if (moreText) moreText.style.display = '';
            if (lessText) lessText.style.display = 'none';
          }
        });
      });
    }
  };

  // ==========================================================================
  // SORT MODULE
  // ==========================================================================
  
  const Sort = {
    init() {
      // Native select for desktop
      const sortSelects = document.querySelectorAll('[data-sort-select]');
      sortSelects.forEach(select => {
        select.addEventListener('change', (e) => {
          this.navigateToSort(e.target.value);
        });
      });

      // Custom dropdown for mobile
      this.initCustomDropdown();
    },

    initCustomDropdown() {
      const dropdowns = document.querySelectorAll('[data-sort-dropdown]');
      
      dropdowns.forEach(dropdown => {
        const trigger = dropdown.querySelector('[data-sort-trigger]');
        const menu = dropdown.querySelector('[data-sort-menu]');
        const options = dropdown.querySelectorAll('[data-sort-option]');
        const currentText = dropdown.querySelector('[data-sort-current]');

        if (!trigger || !menu) return;

        // Toggle dropdown
        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          dropdown.classList.toggle('is-open');
        });

        // Handle option selection
        options.forEach(option => {
          option.addEventListener('click', () => {
            const value = option.dataset.value;
            
            // Update visual state
            options.forEach(opt => opt.classList.remove('is-selected'));
            option.classList.add('is-selected');
            
            // Update trigger text
            if (currentText) {
              currentText.textContent = option.textContent.trim();
            }
            
            // Close dropdown
            dropdown.classList.remove('is-open');
            
            // Navigate
            this.navigateToSort(value);
          });
        });

        // Close on outside click
        document.addEventListener('click', (e) => {
          if (!dropdown.contains(e.target)) {
            dropdown.classList.remove('is-open');
          }
        });
      });
    },

    navigateToSort(value) {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', value);
      window.location.href = url.toString();
    }
  };

  // ==========================================================================
  // CATEGORY BUTTONS MODULE
  // ==========================================================================
  
  const CategoryButtons = {
    init() {
      const buttons = document.querySelectorAll('.category-btn');
      buttons.forEach(btn => {
        btn.addEventListener('click', (e) => {
          // Toggle active state visually (actual navigation handled by onclick)
          buttons.forEach(b => b.classList.remove('category-btn--active'));
          btn.classList.add('category-btn--active');
        });
      });
    }
  };

  // ==========================================================================
  // READ MORE MODULE
  // ==========================================================================
  
  const ReadMore = {
    init() {
      const readMoreLinks = document.querySelectorAll('[data-read-more]');
      readMoreLinks.forEach(link => {
        const container = link.closest('.collection-header__description');
        const textSpan = container.querySelector('.collection-header__description-text');
        const readMoreText = link.querySelector('.read-more-text');
        const readLessText = link.querySelector('.read-less-text');
        
        if (!textSpan || !textSpan.dataset.fullDescription) return;
        
        const fullText = textSpan.dataset.fullDescription;
        let truncatedText = textSpan.textContent;
        const isMobile = window.matchMedia('(max-width: 767px)').matches;

        // On mobile, prefer full text + CSS line-clamp (2 lines)
        if (isMobile) {
          truncatedText = fullText;
          textSpan.innerHTML = fullText;
        }

        container.classList.remove('is-expanded');
        let isExpanded = false;
        
        link.addEventListener('click', (e) => {
          e.preventDefault();
          
          if (isExpanded) {
            // Collapse
            textSpan.innerHTML = truncatedText;
            if (readMoreText) readMoreText.style.display = '';
            if (readLessText) readLessText.style.display = 'none';
            container.classList.remove('is-expanded');
            isExpanded = false;
          } else {
            // Expand
            textSpan.innerHTML = fullText;
            if (readMoreText) readMoreText.style.display = 'none';
            if (readLessText) readLessText.style.display = '';
            container.classList.add('is-expanded');
            isExpanded = true;
          }
        });
      });
    }
  };

  // ==========================================================================
  // ADD TO CART MODULE
  // ==========================================================================
  
  const AddToCart = {
    init() {
      this.bindEvents();
    },

    bindEvents() {
      // Use event delegation for cart buttons
      document.addEventListener('click', (e) => {
        const cartBtn = e.target.closest('.product-card__cart-btn');
        if (cartBtn) {
          e.preventDefault();
          e.stopPropagation();
          this.addToCart(cartBtn);
        }
      });
    },

    async addToCart(button) {
      const variantId = button.dataset.productId;
      if (!variantId) {
        console.error('No variant ID found on cart button');
        return;
      }

      // Disable button and show loading state
      button.disabled = true;
      button.classList.add('is-loading');
      const originalContent = button.innerHTML;
      button.innerHTML = '<span class="loading-spinner"></span>';

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            id: parseInt(variantId),
            quantity: 1
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.description || 'Failed to add to cart');
        }

        const data = await response.json();
        
        // Show success state
        button.classList.remove('is-loading');
        button.classList.add('is-success');
        button.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
        `;

        // Update cart count in header if element exists
        this.updateCartCount();

        // Trigger cart drawer or notification if available
        this.triggerCartUpdate(data);

        // Reset button after delay
        setTimeout(() => {
          button.classList.remove('is-success');
          button.innerHTML = originalContent;
          button.disabled = false;
        }, 2000);

      } catch (error) {
        console.error('Add to cart error:', error);
        
        // Show error state
        button.classList.remove('is-loading');
        button.classList.add('is-error');
        button.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        `;

        // Reset button after delay
        setTimeout(() => {
          button.classList.remove('is-error');
          button.innerHTML = originalContent;
          button.disabled = false;
        }, 2000);
      }
    },

    async updateCartCount() {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();
        
        // Update all cart count elements (including header-cart-badge)
        const cartCountElements = document.querySelectorAll('[data-cart-count], .cart-count-bubble, .cart-count, .header-cart-badge');
        cartCountElements.forEach(el => {
          el.textContent = cart.item_count;
          if (cart.item_count > 0) {
            el.style.display = '';
          }
        });
      } catch (error) {
        console.error('Failed to update cart count:', error);
      }
    },

    triggerCartUpdate(item) {
      // Dispatch custom event for cart drawer or other components
      document.dispatchEvent(new CustomEvent('cart:item-added', {
        detail: { item },
        bubbles: true
      }));

      // Try to refresh and open cart drawer if it exists
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        // Fetch updated cart drawer section HTML using Shopify's section rendering API
        this.refreshCartDrawer(cartDrawer, item);
      }

      // Try to show cart notification if it exists
      const cartNotification = document.querySelector('cart-notification');
      if (cartNotification && typeof cartNotification.renderContents === 'function') {
        this.refreshCartNotification(cartNotification);
      }
    },

    async refreshCartDrawer(cartDrawer, item) {
      try {
        // Use Shopify section rendering to get fresh cart drawer content
        const sections = 'cart-drawer,cart-icon-bubble';
        const response = await fetch(`${window.location.pathname}?sections=${sections}`);
        
        if (!response.ok) throw new Error('Failed to fetch cart sections');
        
        const sectionsData = await response.json();
        
        // Update cart drawer content
        if (sectionsData['cart-drawer']) {
          const cartDrawerElement = document.getElementById('CartDrawer');
          if (cartDrawerElement) {
            const parser = new DOMParser();
            const doc = parser.parseFromString(sectionsData['cart-drawer'], 'text/html');
            const newContent = doc.querySelector('#CartDrawer');
            if (newContent) {
              cartDrawerElement.innerHTML = newContent.innerHTML;
            }
          }
        }
        
        // Update cart icon bubble
        if (sectionsData['cart-icon-bubble']) {
          const cartIconBubble = document.getElementById('cart-icon-bubble');
          if (cartIconBubble) {
            const parser = new DOMParser();
            const doc = parser.parseFromString(sectionsData['cart-icon-bubble'], 'text/html');
            const newIconContent = doc.querySelector('.shopify-section');
            if (newIconContent) {
              // Update just the badge, not the entire icon
              const newBadge = newIconContent.querySelector('.header-cart-badge, .cart-count-bubble');
              const existingBadge = cartIconBubble.querySelector('.header-cart-badge, .cart-count-bubble');

              if (newBadge && existingBadge) {
                existingBadge.textContent = newBadge.textContent;
                existingBadge.style.display = '';
              } else if (newBadge && !existingBadge) {
                cartIconBubble.appendChild(newBadge.cloneNode(true));
              } else if (!newBadge && existingBadge) {
                existingBadge.remove();
              }
            }
          }
        }

        // Ensure the drawer state matches the real cart state.
        // When the cart was previously empty, the <cart-drawer> element can keep `is-empty`
        // even after its inner HTML is refreshed, which hides content and looks "blank".
        try {
          const cartResponse = await fetch('/cart.js');
          const cart = await cartResponse.json();
          const isEmpty = cart.item_count === 0;
          cartDrawer.classList.toggle('is-empty', isEmpty);
          cartDrawer.querySelector('cart-drawer-items')?.classList.toggle('is-empty', isEmpty);
        } catch (e) {
          console.warn('Failed to sync cart drawer empty state:', e);
        }
        
        // Re-bind overlay click event and open drawer
        const overlay = cartDrawer.querySelector('#CartDrawer-Overlay');
        if (overlay) {
          overlay.addEventListener('click', () => cartDrawer.close());
        }
        
        // Open the cart drawer
        if (typeof cartDrawer.open === 'function') {
          cartDrawer.open();
        }
        
      } catch (error) {
        console.error('Failed to refresh cart drawer:', error);
        // Fallback: just open the drawer without refreshing
        if (typeof cartDrawer.open === 'function') {
          cartDrawer.open();
        }
      }
    },

    async refreshCartNotification(cartNotification) {
      try {
        const sections = 'cart-notification';
        const response = await fetch(`${window.location.pathname}?sections=${sections}`);
        
        if (!response.ok) throw new Error('Failed to fetch notification section');
        
        const sectionsData = await response.json();
        
        if (sectionsData['cart-notification'] && typeof cartNotification.renderContents === 'function') {
          cartNotification.renderContents({ sections: sectionsData });
        }
      } catch (error) {
        console.error('Failed to refresh cart notification:', error);
      }
    }
  };

  // ==========================================================================
  // SIZE PILLS (Collection Cards)
  // ==========================================================================

  const SizePills = {
    resizeTimer: null,

    init() {
      this.fitAll();
      window.addEventListener('resize', () => {
        window.clearTimeout(this.resizeTimer);
        this.resizeTimer = window.setTimeout(() => this.fitAll(), 120);
      });
    },

    fitAll() {
      const containers = document.querySelectorAll('[data-size-pills]');
      if (!containers.length) return;
      containers.forEach((container) => this.fitContainer(container));
    },

    fitContainer(container) {
      const pills = Array.from(container.querySelectorAll('[data-size-pill]'));
      const more = container.querySelector('[data-sizes-more]');
      if (!pills.length || !more) return;

      // Reset
      pills.forEach((pill) => pill.removeAttribute('hidden'));
      more.setAttribute('hidden', '');
      more.textContent = '+0';

      const isTabletOrMobile = window.matchMedia('(max-width: 1024px)').matches;

      // Tablet/Mobile requirement: show max 2 pills
      if (isTabletOrMobile) {
        // Start with 2 pills (or fewer if product has fewer)
        const visibleCount = Math.min(2, pills.length);

        // Always hide beyond visibleCount
        pills.forEach((pill, idx) => {
          if (idx >= visibleCount) pill.setAttribute('hidden', '');
        });

        // Show +N when more exist
        let hiddenCount = pills.length - visibleCount;
        if (hiddenCount > 0) {
          more.textContent = `+${hiddenCount}`;
          more.removeAttribute('hidden');
        } else {
          more.setAttribute('hidden', '');
          return;
        }

        return;
      }

      // Desktop: If only a couple pills, nothing to do
      if (pills.length <= 2) return;

      const containerWidth = container.clientWidth;
      if (!containerWidth) return;

      const style = window.getComputedStyle(container);
      const gap = parseFloat(style.columnGap || style.gap || '0') || 0;

      // Measure if everything fits
      const totalWidth = pills.reduce((acc, pill, idx) => acc + pill.offsetWidth + (idx ? gap : 0), 0);
      if (totalWidth <= containerWidth) return;

      // Show "more" to measure its width
      more.removeAttribute('hidden');
      more.textContent = '+0';
      const moreWidth = more.offsetWidth;

      // Compute how many pills we can show alongside the more link
      let used = 0;
      let visibleCount = 0;

      for (let i = 0; i < pills.length; i++) {
        const pill = pills[i];
        const pillWidth = pill.offsetWidth;
        const nextUsed = used + pillWidth + (visibleCount ? gap : 0);
        const required = nextUsed + gap + moreWidth; // gap between last pill and +N

        if (visibleCount === 0) {
          // Always allow at least one pill; we'll keep +N even if cramped
          used = nextUsed;
          visibleCount = 1;
          continue;
        }

        if (required <= containerWidth) {
          used = nextUsed;
          visibleCount += 1;
        } else {
          break;
        }
      }

      pills.forEach((pill, idx) => {
        if (idx >= visibleCount) pill.setAttribute('hidden', '');
      });

      const hiddenCount = pills.length - visibleCount;
      if (hiddenCount > 0) {
        more.textContent = `+${hiddenCount}`;
        more.removeAttribute('hidden');
      } else {
        more.setAttribute('hidden', '');
      }
    }
  };

  // ==========================================================================
  // INITIALIZATION
  // ==========================================================================
  
  const CollectionPage = {
    init() {
      FilterDrawer.init();
      FilterHandler.init();
      PriceSlider.init();
      Accordion.init();
      BrandFilter.init();
      ColorFilter.init();
      Sort.init();
      CategoryButtons.init();
      ReadMore.init();
      SizePills.init();
      AddToCart.init();
    }
  };

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => CollectionPage.init());
  } else {
    CollectionPage.init();
  }

  // Expose for external use if needed
  window.CollectionPage = CollectionPage;

})();
